import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../../constants/routes';
import { DISPLAY, UI, EASE, lead, lnk } from '../../styles/tl5';
import { useGetPageSectionsQuery } from '../../store/siteContentApi';
import heroImg from '../../assets/img/v5/hero.jpg';

// Hero — two panels side by side: a white card carrying the headline and
// four entry points, and a tall image panel with one status card.
//
// Three things the reference does NOT have, which this used to:
//   · the Patient / Doctor-or-hospital role toggle. The reference still
//     defines both door sets in its data but renders only the patient
//     four — the doctor paths moved to the Partners pages. DOCTOR_DOORS
//     is kept below so the toggle can be restored in one step.
//   · a "LIVE · Trustian on the way" chip in the top-right of the image.
//   · a hand-drawn underline under "answered."
//
// The entrance animations are kept: the reference is static HTML and so
// has no way to express timing, and none of them changes the layout.
//
// Copy is admin-editable from `GET /api/page-sections/home` → `hero`.
// Everything below stays as the fallback and must keep matching the
// controller's DEFAULTS, so a slow or failing call renders exactly what
// shipped instead of an empty hero. Layout, tones and animation stay in
// code — only the words and the four door targets are editable.

// Fallback copy — mirrors pageSectionController's DEFAULTS.home.hero.
const FALLBACK = {
  badge: 'NABL · ISO 15189:2022',
  headline_line1: 'Your health,',
  headline_line2: 'answered.',
  lede: 'A Trustian at your door in 60 minutes. A doctor-verified report on WhatsApp the same day, guaranteed.*',
  footnote: '*Conditions apply. Most routine tests, sample collected before 12 noon.',
  image_alt: 'A stethoscope on a desk beside a laptop',
  proof_title: 'Report verified by a doctor',
  proof_sub: 'Sent to WhatsApp · 12:20 pm',
};

const DOORS = [
  { t: 'Book a home test', s: 'Trustian in 60 minutes', href: ROUTES.BOOKING, tone: 'dark' },
  { t: 'Build your own package', s: 'Start from what worries you', href: ROUTES.BUILD_PACKAGE, tone: 'yellow' },
  { t: 'Get my report', s: 'Mobile number or UHID', href: ROUTES.REPORTS, tone: 'mint' },
  { t: 'Find a test', s: 'Prices and preparation', href: ROUTES.TESTS, tone: 'mint' },
];

// Unrendered — see the note above.
export const DOCTOR_DOORS = [
  { t: 'Refer a patient', s: 'WhatsApp booking link in seconds', href: ROUTES.MED_DASHBOARD, tone: 'dark' },
  { t: 'Access patient results', s: 'Reports and critical alerts', href: ROUTES.MED_DASHBOARD, tone: 'teal' },
  { t: 'Directory of Services', s: 'Method, sample and TAT', href: ROUTES.TESTS, tone: 'mint' },
  { t: 'Become a partner', s: 'Hospitals, clinics and labs', href: ROUTES.FRANCHISE, tone: 'mint' },
];

const TONES = {
  dark: { bgcolor: 'var(--tl-g9)', color: '#fff' },
  yellow: { bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)' },
  teal: { bgcolor: 'var(--tl-teal)', color: '#fff' },
  mint: { bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)', '&:hover': { bgcolor: 'var(--tl-mint-2)' } },
};

// `dir` just rotates the one glyph, so a back/up/down arrow is the same
// stroke weight and cap as every forward arrow on the site.
const ARROW_TURN = { right: 0, down: 90, left: 180, up: -90 };

const Arrow = ({ size = 16, dir = 'right' }) => (
  <Box
    component="svg"
    className="tl-arr"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={{
      display: 'block',
      width: size,
      height: size,
      minWidth: size,
      flex: 'none',
      ...(ARROW_TURN[dir] ? { transform: `rotate(${ARROW_TURN[dir]}deg)` } : null),
    }}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Box>
);

const Hero = () => {
  // One request for the whole page's copy; RTK Query dedupes it across
  // the other sections that will read from it later.
  const { data: sections } = useGetPageSectionsQuery('home');
  const c = { ...FALLBACK, ...(sections?.hero || {}) };
  // An admin who empties the list should not be left with no way in, so
  // the shipped doors stand in for an empty one.
  const doors = Array.isArray(sections?.hero?.doors) && sections.hero.doors.length
    ? sections.hero.doors
    : DOORS;

  return (
  <Box component="section" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', pt: 2, px: 2 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
      {/* ── left: the card ─────────────────────────────────────────── */}
      <Box
        sx={{
          flex: '1.3 1 520px',
          minWidth: 0,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          p: 'clamp(22px, 3.4vw, 40px)',
          borderRadius: '36px',
          bgcolor: '#FFFFFF',
          animation: `tl-rise .9s ${EASE} both`,
          // Soft teal bloom bleeding in from the top-right corner.
          '&::before': {
            content: '""',
            position: 'absolute',
            right: -120,
            top: -120,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(95,224,198,.22), rgba(95,224,198,0) 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, position: 'relative' }}>
          {/* An admin who clears the badge wants no pill, not an empty one. */}
          {c.badge ? (
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                px: '12px',
                py: '7px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-mint)',
                color: 'var(--tl-teal-d)',
                fontFamily: UI,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {c.badge}
            </Box>
          ) : null}

          {/* Each line sits in its own clipped band and rises into it. */}
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(36px, 5vw, 64px)',
              lineHeight: 0.95,
              letterSpacing: '-.035em',
              textWrap: 'balance',
              color: 'var(--tl-g9)',
            }}
          >
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', pb: '.1em', mb: '-.1em' }}>
              <Box component="span" sx={{ display: 'inline-block', animation: `tl-lineup 1s ${EASE} both` }}>
                {c.headline_line1}
              </Box>
            </Box>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', pb: '.1em', mb: '-.1em' }}>
              <Box
                component="span"
                sx={{ display: 'inline-block', color: 'var(--tl-teal)', animation: `tl-lineup 1s ${EASE} 110ms both` }}
              >
                {c.headline_line2}
              </Box>
            </Box>
          </Typography>

          <Typography sx={{ ...lead, fontSize: { xs: 16.5, md: 18 }, maxWidth: 520, animation: `tl-rise .8s ${EASE} .35s both` }}>
            {c.lede}
          </Typography>

          <Box component="span" sx={{ fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-muted-2)', maxWidth: 520 }}>
            {c.footnote}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
              gap: 1,
            }}
          >
            {doors.map((d, i) => (
              <Box
                key={d.t}
                component={Link}
                to={d.href}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minHeight: 96,
                  p: '18px',
                  borderRadius: '20px',
                  textDecoration: 'none',
                  transition: `transform .45s ${EASE}, box-shadow .45s ${EASE}`,
                  animation: `tl-stgin .6s ${EASE} both`,
                  animationDelay: `${i * 70}ms`,
                  ...(TONES[d.tone] || TONES.mint),
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'var(--tl-sh-2)',
                    ...((TONES[d.tone] || TONES.mint)['&:hover'] || {}),
                    color: (TONES[d.tone] || TONES.mint).color,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}
                >
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>
                    {d.t}
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800 }}>→</Box>
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.45, opacity: 0.85 }}>
                  {d.s}
                </Box>
              </Box>
            ))}
          </Box>

          <Box component={Link} to={ROUTES.DOCTOR_CONSULT} sx={{ ...lnk, alignSelf: 'start' }}>
            Not sure what to test? Talk to a lab doctor first
            <Arrow size={15} />
          </Box>
        </Box>
      </Box>

      {/* ── right: the image panel ─────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          flex: '1 1 380px',
          minWidth: 0,
          minHeight: { xs: 280, sm: 340, md: 380 },
          display: 'flex',
          alignItems: 'flex-end',
          borderRadius: '36px',
          overflow: 'hidden',
          bgcolor: 'var(--tl-g9)',
          animation: `tl-reveal 1.2s ${EASE} .15s both`,
        }}
      >
        <Box
          component="img"
          src={heroImg}
          alt={c.image_alt}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            animation: 'tl-kenburns 22s ease-in-out infinite alternate',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            m: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            p: '16px 18px',
            borderRadius: '22px',
            bgcolor: 'rgba(255,255,255,0.96)',
            color: 'var(--tl-g9)',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: 'var(--tl-teal)',
              color: '#FFFFFF',
              fontFamily: UI,
              fontWeight: 800,
            }}
          >
            ✓
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>
              {c.proof_title}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
              {c.proof_sub}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
  );
};

export default Hero;
export { Arrow, DISPLAY };
